/**
 * Auth store (Zustand) + bootstrap helper.
 *
 * Single source of truth on the client for "who is logged in". The cookie is
 * the actual auth credential — this store just mirrors what the server says
 * about the current session so the UI can render the right shell.
 */

import { create } from "zustand";

import { ApiError, type MeResponse, type UserPublic, authApi } from "./api";

type AuthStatus = "unknown" | "anonymous" | "authenticated";

interface AuthState {
  status: AuthStatus;
  user: UserPublic | null;
  sessionExpiresAt: string | null;
  sensitiveOpActive: boolean;

  setMe: (me: MeResponse) => void;
  clear: () => void;
  refresh: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "unknown",
  user: null,
  sessionExpiresAt: null,
  sensitiveOpActive: false,

  setMe: (me) =>
    set({
      status: "authenticated",
      user: me.user,
      sessionExpiresAt: me.session_expires_at,
      sensitiveOpActive: me.sensitive_op_active,
    }),

  clear: () =>
    set({
      status: "anonymous",
      user: null,
      sessionExpiresAt: null,
      sensitiveOpActive: false,
    }),

  refresh: async () => {
    try {
      const me = await authApi.me();
      get().setMe(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        get().clear();
        return;
      }
      throw err;
    }
  },
}));

/**
 * Run once at app startup to populate the store from the server.
 * Returns a promise that resolves when we know whether the user is logged in.
 *
 * Idempotent: subsequent invocations return the in-flight or already-
 * resolved promise instead of re-hitting `/api/auth/me`. The previous
 * version was effectively idempotent because it was only called from
 * the entry script, but we observed a production regression where a
 * stale service-worker cache could trigger the bootstrap repeatedly
 * during a single page session, hitting the backend ~2 req/s. Caching
 * the promise here makes that class of bug impossible regardless of
 * the call site.
 */
let bootstrapPromise: Promise<void> | null = null;

export function bootstrapAuth(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = useAuthStore.getState().refresh();
  return bootstrapPromise;
}

export function getCurrentUser(): UserPublic | null {
  return useAuthStore.getState().user;
}

export function isAuthenticated(): boolean {
  return useAuthStore.getState().status === "authenticated";
}
